'use client';

import { FC } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom, ShipmentWithOrderLines } from '@ravago/shared/page-data/models/elements';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Chip } from '@ravago/shared/radiance/components/chip/components/Chip/Chip';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { SystemPageLink } from '../../content/system-page-link/system-page-link';
import { OrderLineTableMenu } from '../order-lines-table/order-line-table-menu';
import { OrderLineItem } from '../order/order-line-item';

export interface ShipmentTableProps {
   shipments: ShipmentWithOrderLines[];
   isConsumer: boolean;
   locale: string;
   acceptWebp?: boolean;
   orderBaseUrls: OrderBaseUrls;
   openedFrom: OrderLineOpenedFrom;
   isUSBusinessContext: boolean;
   showInvoiceDocuments: boolean;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
}

export const DesktopShipmentTable: FC<{
   shipment: ShipmentWithOrderLines;
   acceptWebp?: boolean;
   locale: string;
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   onOrderLineButtonClick: (orderLine: DisplayOrder) => void;
}> = ({ shipment, acceptWebp, locale, openedFrom, orderBaseUrls, onOrderLineButtonClick }) => {
   const translate = useTranslations();
   const format = useFormatter();

   return (
      <Box direction="column" width="100%">
         <Table rowDividers rounded border>
            <TableHead>
               <TableRow>
                  <TableHeader>
                     <SystemPageLink
                        baseRoute={orderBaseUrls.shipmentBaseUrl}
                        locale={locale}
                        type={SystemPageType.SHIPMENT}
                        slug={shipment.shipmentId.toString()}
                     >
                        <Typography variant="small-highlight" component="span" underline color="primary">
                           {translate('shipment.shipment')} {shipment.shipmentId}
                        </Typography>
                     </SystemPageLink>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('my-orders.order-header')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Box justify="end">
                        <Chip
                           type={
                              <Typography variant="small" component="span" color="strong">
                                 {translate('shipment.delivery-date')}
                              </Typography>
                           }
                           label={
                              <Typography variant="small-highlight" component="span" align="left" color="strong">
                                 {DateUtils.transformOrderDeliveryDate(format, shipment.orderLines[0].deliveryDate)}
                              </Typography>
                           }
                        />
                     </Box>
                  </TableHeader>
               </TableRow>
            </TableHead>
            <TableBody>
               {shipment.orderLines.map((orderLine) => (
                  <TableRow key={orderLine.orderNumber} onClick={() => {}}>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)} width="300px">
                        <OrderLineItem order={orderLine} openedFrom={openedFrom} acceptWebp={acceptWebp} />
                     </TableCell>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)}>
                        <Box direction="column" gap="3xs">
                           <Typography variant="small">
                              {translate('my-orders.table.order-line')} {orderLine.orderNumber}
                           </Typography>
                           <Typography variant="small">
                              {translate('my-orders.table.order-ref')} {orderLine.customerOrderReference || '-'}
                           </Typography>
                        </Box>
                     </TableCell>
                     <TableCell>
                        <Box justify="end">
                           <OrderLineTableMenu
                              orderLine={orderLine}
                              shipmentId={shipment.shipmentId}
                              locale={locale}
                              orderBaseUrls={orderBaseUrls}
                              openedFrom={openedFrom}
                              onOrderLineButtonClick={onOrderLineButtonClick}
                           />
                        </Box>
                     </TableCell>
                  </TableRow>
               ))}
            </TableBody>
         </Table>
      </Box>
   );
};
